import { useCallback, useEffect, useRef, useState } from 'react';
import { pcseService } from '../api/pcse.service';
import {
  PcseClaim,
  PcseClaimStatus,
  PcseClaimSummary,
  PcseFieldError,
  PcsePingResult,
  SubmitClaimPayload,
} from '../@types/pcse';

/**
 * Normalise whatever the axios interceptor rejected with into a message +
 * optional per-field errors. The shared axios instance rejects with
 * `error.response.data` (the Nest error body) or the string 'Something went
 * wrong'. Capita/backend validation errors may arrive as an array of
 * `{ code, field, message }`.
 */
export function normalisePcseError(err: unknown): { message: string; fieldErrors: PcseFieldError[] } {
  if (typeof err === 'string') return { message: err, fieldErrors: [] };
  const body = err as any;
  const fieldErrors: PcseFieldError[] = Array.isArray(body?.errors) ? body.errors : [];
  const message =
    (Array.isArray(body?.message) ? body.message.join(', ') : body?.message) ||
    body?.error ||
    'Something went wrong contacting PCSE';
  return { message, fieldErrors };
}

// ── Submit ─────────────────────────────────────────────────────────────────

export function useSubmitClaim() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PcseFieldError[]>([]);
  const [claim, setClaim] = useState<PcseClaim | null>(null);

  const submit = useCallback(async (payload: SubmitClaimPayload) => {
    setLoading(true);
    setError(null);
    setFieldErrors([]);
    try {
      const res = await pcseService.submitClaim(payload);
      setClaim(res.data);
      return res.data;
    } catch (err) {
      const { message, fieldErrors: fe } = normalisePcseError(err);
      setError(message);
      setFieldErrors(fe);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error, fieldErrors, claim };
}

// ── Status polling ───────────────────────────────────────────────────────────

const TERMINAL: PcseClaimStatus[] = ['accepted', 'rejected'];

/**
 * Fetch and (optionally) poll a claim's status. Polling stops automatically
 * once the claim reaches a terminal state (accepted/rejected) or on unmount.
 */
export function useClaimStatus(claimId?: string, opts: { pollMs?: number; enabled?: boolean } = {}) {
  const { pollMs = 0, enabled = true } = opts;
  const [claim, setClaim] = useState<PcseClaim | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const fetchStatus = useCallback(async () => {
    if (!claimId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await pcseService.getStatus(claimId);
      setClaim(res.data);
      return res.data;
    } catch (err) {
      setError(normalisePcseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    if (!claimId || !enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      const result = await fetchStatus();
      if (cancelled) return;
      const isTerminal = result ? TERMINAL.includes(result.status) : false;
      if (pollMs > 0 && !isTerminal) {
        timer.current = setTimeout(tick, pollMs);
      }
    };
    tick();

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [claimId, enabled, pollMs, fetchStatus]);

  return { claim, loading, error, refresh: fetchStatus };
}

// ── Claim list ───────────────────────────────────────────────────────────────

export function usePatientClaims(patientId?: string) {
  const [claims, setClaims] = useState<PcseClaimSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await pcseService.listClaims({ patientId });
      setClaims(res.data.data);
    } catch (err) {
      setError(normalisePcseError(err).message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  return { claims, loading, error, reload: load };
}

// ── Connectivity ─────────────────────────────────────────────────────────────

export function usePcsePing() {
  const [result, setResult] = useState<PcsePingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ping = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pcseService.ping();
      setResult(res.data);
      return res.data;
    } catch (err) {
      setError(normalisePcseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { ping, result, loading, error };
}
